import types from "../types";
import { copy, Notification, url_encode } from "../../utils";
import backend from "../../api";
import { global_actions } from "..";

// Action to create an Ad Card
const create_adcard = ({ product = null, expiry_date = null } = {}) => async (
  dispatch,
  getState
) => {
  let state = copy(getState().adcard);
  let {
    user: { user },
  } = copy(getState().auth);
  const status = new Notification();
  dispatch(global_actions.loading(true));

  const adcardData = Array.isArray(state.data) ? state.data : [];

  try {
    // Check if an active ad card already exists for this product and member
    const existingAdcards = adcardData.filter(
      (ad) =>
        ad.product._id === product &&
        ad.isActive &&
        ad.status !== "Expired" &&
        ad.member_user._id === user._id // Ensure the ad card is for the specific member
    );

    if (existingAdcards.length > 0) {
      status.set_toast(
        "error",
        "An active ad card already exists for this product."
      );
      status.show_toast();
      dispatch(global_actions.loading(false));
      return;
    }

    const temporaryLink = `${
      process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_FRONTEND
        : process.env.REACT_APP_FRONTEND_DEV
    }/product/${product}?promoted_by=${user._id}&id=temp`;

    const { data } = await backend.post(
      "/sharedlink",
      {
        product,
        expiry_date,
        member_user: user._id,
        sharedlink_date: new Date(),
        shareable_link: temporaryLink,
        status: "Active",
      },
      {
        params: {
          populate: url_encode([
            {
              path: "product",
              populate: [{ path: "merchant_user", select: "-password" }],
            },
            {
              path: "member_user",
              select: "-password",
            },
          ]),
        },
      }
    );

    const newAdcard = data.data;
    state.data = Array.isArray(state.data)
      ? state.data.concat([newAdcard])
      : [newAdcard];

    const finalLink = `${
      process.env.NODE_ENV === "production"
        ? process.env.REACT_APP_FRONTEND
        : process.env.REACT_APP_FRONTEND_DEV
    }/product/${newAdcard.product._id}?promoted_by=${user._id}&id=${
      newAdcard._id
    }`;

    const updatedAdcard = {
      ...newAdcard,
      shareable_link: finalLink,
    };

    await backend.put(`/sharedlink/${newAdcard._id}`, updatedAdcard, {
      params: {
        populate: url_encode([
          {
            path: "product",
            populate: [{ path: "merchant_user", select: "-password" }],
          },
          {
            path: "member_user",
            select: "-password",
          },
        ]),
      },
    });

    state.data = state.data
      .map((c) => (c._id === newAdcard._id ? updatedAdcard : c))
      .filter((i) => i.isActive && i.status !== "Expired");

    dispatch({
      type: types.ADCARD,
      payload: state,
    });

    status.set_toast("success", data.message);
  } catch (error) {
    status.set_toast(
      "error",
      error.response ? error.response.data.message : error.message
    );
  } finally {
    dispatch(global_actions.loading(false));
    status.show_toast();
  }
};

// Action to fetch ad cards
const fetch_adcard = (query) => async (dispatch, getState) => {
  let state = copy(getState().adcard);
  const status = new Notification();
  dispatch(global_actions.loading(true));

  try {
    const { data } = await backend.get("/sharedlink", {
      params: {
        ...query,
        populate: url_encode([
          {
            path: "product",
            populate: [{ path: "merchant_user", select: "-password" }],
          },
          {
            path: "member_user",
            select: "-password",
          },
        ]),
      },
    });

    const payload = {
      data: data.data,
      totalPages: data.totalPages,
    };

    dispatch({
      type: types.ADCARD,
      payload: payload,
    });

    return payload;
  } catch (error) {
    status.set_toast(
      "error",
      error.response ? error.response.data.message : error.message
    );
  } finally {
    dispatch(global_actions.loading(false));
    status.show_toast();
  }
};

// Action to update an ad card
const update_adcard = (update) => async (dispatch, getState) => {
  let state = copy(getState().adcard);
  const status = new Notification();
  dispatch(global_actions.loading(true));

  try {
    const { data } = await backend.put(
      `/sharedlink/${update._id}`,
      { ...update, _id: undefined },
      {
        params: {
          populate: url_encode([
            {
              path: "product",
              populate: [{ path: "merchant_user", select: "-password" }],
            },
            {
              path: "member_user",
              select: "-password",
            },
          ]),
        },
      }
    );

    if (!Array.isArray(state.data)) {
      state.data = [];
    }

    state.data = state.data
      .map((c) => (c._id === update._id ? data.data : c))
      .filter((i) => i.isActive && i.status !== "Expired");

    dispatch({
      type: types.ADCARD,
      payload: state,
    });

    status.set_toast("success", data.message);
  } catch (error) {
    status.set_toast(
      "error",
      error.response ? error.response.data.message : error.message
    );
  } finally {
    dispatch(global_actions.loading(false));
    status.show_toast();
  }
};

export default { create_adcard, fetch_adcard, update_adcard };
